import { DatePipe } from '@angular/common';
import { Component, EventEmitter, Input, Output, ViewChild, OnInit, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { Subject, takeUntil, catchError, of, finalize } from 'rxjs';
import moment from 'moment';
import { MyConfigOrganization } from '../../../../../models/myConfig';
import { TransactionVM, TransactionPaginatedModel } from '../../../../../models/orderModel';
import { BackOfficeService } from '../../../../../services/backoffice.service';

@Component({
  selector: 'app-transaction-list-paginated',
  standalone: false,
  templateUrl: './transaction-list-paginated.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './transaction-list-paginated.component.css'
})
export class TransactionListPaginatedComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  @Output() incrementRequests: EventEmitter<void> = new EventEmitter<void>();
  @Output() decrementRequests: EventEmitter<void> = new EventEmitter<void>();
  @Input() isLoading: boolean = false;
  @Input() organization: MyConfigOrganization;

  // Form and data
  transactionForm: UntypedFormGroup;
  transactions: TransactionVM[] = [];
  dataSource: MatTableDataSource<TransactionVM>;
  totalData: number = 0;
  pageSizes: number[] = [50, 100, 150, 250];

  // UI state
  filtersExpanded: boolean = true;

  // Search properties
  selectedSearchType: string = 'orderNr';
  currentSearchValue: string = '';

  // Date defaults
  dateFrom: string;
  dateTo: string;
  timeFrom: string = "00:00";
  timeTo: string = "00:00";

  // Filter options
  availablePaymentStatuses: string[] = [
    "New",
    "CHECKOUT",
    "Reserved",
    "Paid",
    "Captured",
    "Reversed",
    "DELETED",
    "Cancelled",
  ];

  availablePaymentTypes: string[] = [
    "Swish",
    "Not yet Choosen",
    "External",
    "New",
    "CreditCard"
  ];

  availableSubOrgs: { id: string; label: string }[] = [];

  displayedColumns: string[] = [
    'orderNr',
    'transactionNr',
    'organizationName',
    'amount',
    'vatAmount',
    'amountIncVat',
    'paymentStatus',
    'paymentType',
    'paymentDate',
    'debitCreditType',
    'created'
  ];

  // Column mapping for sorting
  private readonly columnMapping: { [key: string]: string } = {
    'orderNr': 'OrderNr',
    'transactionNr': 'TransactionNr',
    'amount': 'Amount',
    'vatAmount': 'VatAmount',
    'amountIncVat': 'AmountIncVat',
    'paymentStatus': 'PaymentStatus',
    'paymentType': 'PaymentType',
    'paymentDate': 'PaymentDate',
    'debitCreditType': 'DebitCreditType',
    'created': 'Created'
  };

  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  @ViewChild(MatSort, { static: false }) set matSort(sort: MatSort) {
    if (sort) {
      this.sort = sort;
      this.setupSorting();
    }
  }
  sort: MatSort;

  constructor(
    public backOfficeService: BackOfficeService,
    public dialog: MatDialog,
    public router: Router,
    private fb: UntypedFormBuilder,
    public datepipe: DatePipe,
    public snackBar: MatSnackBar
  ) { }

  ngOnInit(): void {
    this.getSubOrgs();
    this.setDefaultDates();
    this.initializeForm();
    if (!this.organization?.organizationId) {
      this.organization = {
        ...this.organization,
        organizationId: 'a848ccd3-fd8d-4035-925e-571ebb4e93e8'
      };
    }
    this.setTransactionMatTableData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getSubOrgs() {
    this.organization.config.subOrganizations.forEach(org => {
      if( org.typeName !== "ServicePerformer"){
        this.availableSubOrgs.push({
          id: org.organizationId,
          label: org.name
        });
      }
    });
  }

  private setDefaultDates(): void {
    this.dateFrom = moment().add(-10, 'd').format();
    this.timeFrom = "00:00";
    this.dateTo = moment().add(1, 'd').format();
    this.timeTo = "00:00";
  }

  private initializeForm(): void {
    this.transactionForm = this.fb.group({
      dateFrom: [{ value: moment().add(-10, 'd').toDate(), disabled: this.isLoading }],
      dateTo: [{ value: moment().add(1, 'd').toDate(), disabled: this.isLoading }],
      paymentStatusFilter: [{ value: [], disabled: this.isLoading }],
      paymentTypeFilter: [{ value: [], disabled: this.isLoading }],
      subOrgIdsFilter: [{ value: [], disabled: this.isLoading }]
    });
  }

  onSearchTypeChange(): void {
    this.currentSearchValue = '';
  }

  executeSearch(): void {
    if (this.currentSearchValue?.trim()) {
      this.applyFilters();
    }
  }

  clearSearch(): void {
    this.currentSearchValue = '';
    this.applyFilters();
  }

  getSearchPlaceholder(): string {
    switch (this.selectedSearchType) {
      case 'orderNr': return 'TRANSACTION.SEARCH.PLACEHOLDER_ORDER_NUMBER';
      case 'transactionNr': return 'TRANSACTION.SEARCH.PLACEHOLDER_TRANSACTION_NUMBER';
      default: return 'TRANSACTION.SEARCH.PLACEHOLDER';
    }
  }

  getSearchIcon(): string {
    switch (this.selectedSearchType) {
      case 'orderNr': return 'receipt';
      case 'transactionNr': return 'payment';
      default: return 'search';
    }
  }

  applyFilters(): void {
    // Cancel any ongoing requests
    this.destroy$.next();

    if (this.paginator) {
      this.paginator.pageIndex = 0;
      this.paginator.page.pipe(
        takeUntil(this.destroy$)
      ).subscribe(() => {
        console.log("page change");
        this.loadData();
      });
    }


    this.loadData();
  }

  clearFilters(): void {
    this.currentSearchValue = '';
    this.selectedSearchType = 'orderNr';
    this.setDefaultDates();
    this.transactionForm.patchValue({
      dateFrom: moment().add(-10, 'd').toDate(),
      dateTo: moment().add(1, 'd').toDate(),
      paymentStatusFilter: [],
      paymentTypeFilter: [],
      subOrgIdsFilter: []
    });
    this.applyFilters();
  }

  getActiveFiltersCount(): number {
    let count = 0;
    if (this.currentSearchValue?.trim()) count++;

    const currentDateFrom = this.transactionForm.get('dateFrom')?.value;
    const currentDateTo = this.transactionForm.get('dateTo')?.value;
    const defaultDateFrom = moment().add(-10, 'd').toDate();
    const defaultDateTo = moment().add(1, 'd').toDate();

    if (currentDateFrom && !moment(currentDateFrom).isSame(defaultDateFrom, 'day')) count++;
    if (currentDateTo && !moment(currentDateTo).isSame(defaultDateTo, 'day')) count++;
    if (this.transactionForm.get('paymentStatusFilter')?.value?.length > 0) count++;
    if (this.transactionForm.get('paymentTypeFilter')?.value?.length > 0) count++;
    if (this.transactionForm.get('subOrgIdsFilter')?.value?.length > 0) count++;
    return count;
  }

  toggleFilters(): void {
    this.filtersExpanded = !this.filtersExpanded;
  }

  private setFilters({
    sortingColumn = 'created',
    sortingDirection = 'desc',
    perPage,
    page
  }: {
    sortingColumn?: string;
    sortingDirection?: 'asc' | 'desc';
    perPage: number;
    page: number;
  }): TransactionPaginatedModel {

    const result: Partial<TransactionPaginatedModel> = {
      Paginate: {
        PerPage: perPage,
        Page: page
      }
    };

    if (this.currentSearchValue?.trim()) {
      switch (this.selectedSearchType) {
        case 'orderNr':
          result.OrderNrSearch = this.currentSearchValue;
          break;
        case 'transactionNr':
          result.TransactionNrSearch = this.currentSearchValue;
          break;
      }
    }

    const dateFrom = this.transactionForm.get('dateFrom')?.value;
    const dateTo = this.transactionForm.get('dateTo')?.value;
    if (dateFrom || dateTo) {
      result.Created = {
        From: dateFrom ? this.datepipe.transform(dateFrom, 'yyyy-MM-dd') || '' : '',
        To: dateTo ? this.datepipe.transform(dateTo, 'yyyy-MM-dd') || '' : ''
      };
    }

    const paymentStatuses = this.transactionForm.get('paymentStatusFilter')?.value;
    if (paymentStatuses && paymentStatuses.length > 0) {
      result.PaymentStatuses = paymentStatuses;
    }

    const paymentTypes = this.transactionForm.get('paymentTypeFilter')?.value;
    if (paymentTypes && paymentTypes.length > 0) {
      result.PaymentType = paymentTypes.join(',');
    }

    const subOrgIds = this.transactionForm.get('subOrgIdsFilter')?.value;
    if (subOrgIds && subOrgIds.length > 0) {
      result.OrganizationIds = subOrgIds;
    } else {
      result.OrganizationIds = [this.organization.organizationId, ...this.availableSubOrgs.map(org => org.id)];
    }

    return result as TransactionPaginatedModel;
  }

  private setTransactionMatTableData(): void {
    if (!this.organization) return;

    if (this.paginator) {
      this.paginator.page.pipe(
        takeUntil(this.destroy$)
      ).subscribe(() => {
        this.loadData();
      });
    }

    this.setupSorting();

    this.loadData();
  }

  private setupSorting(): void {
    if (this.sort) {
      if (!this.sort.active) {
        this.sort.active = 'created';
        this.sort.direction = 'desc';
        this.sort.sortChange.emit({
          active: this.sort.active,
          direction: this.sort.direction
        });
      }

      this.sort.sortChange.pipe(
        takeUntil(this.destroy$)
      ).subscribe(() => {
        if (this.paginator) {
          this.paginator.pageIndex = 0;
        }
        this.loadData();
      });
    }
  }

  private loadData(): void {
    if (!this.organization) return;

    const sortActive = this.sort?.active || 'created';
    const sortDirection = this.sort?.direction || 'desc';

    const sortColumn = this.columnMapping[sortActive] || sortActive;

    this.incrementRequests.emit();

    this.backOfficeService.getTransactionsPaginated(
      this.organization.organizationId,
      this.setFilters({
        perPage: this.paginator?.pageSize || 50,
        page: this.paginator ? this.paginator.pageIndex + 1 : 1,
        sortingColumn: sortColumn,
        sortingDirection: sortDirection as 'asc' | 'desc'
      })
    ).pipe(
      takeUntil(this.destroy$),
      catchError(error => {
        console.error('Error loading transactions:', error);
        this.snackBar.open(`Error: ${error.status}`, 'Close', {
          verticalPosition: 'top'
        });
        return of({ data: [], pagination: { total: 0 } });
      }),
      finalize(() => this.decrementRequests.emit())
    ).subscribe((response: any) => {
      if (!response) return;

      this.transactions = response.data || response || [];
      this.totalData = response.pagination?.total || response.length || 0;

      if (!this.dataSource) {
        this.dataSource = new MatTableDataSource<TransactionVM>(this.transactions);
        this.dataSource.paginator = this.paginator;
      } else {
        this.dataSource.connect().next(this.transactions);
      }
    });
  }

  getTransactions(): void {
    this.loadData();
  }

  goToOrder(orderId: string): void {
    const url = this.router.serializeUrl(this.router.createUrlTree([`/admin/back-office24-order/${orderId}`]));
    window.open(url, '_blank');
  }
}
